import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import Header from '../Layout/Header';
import { ChatProvider, useChatContext } from '../../context/ChatContext';
import { FileTreeProvider } from '../../context/FileTreeContext';
import {
  ArtifactsPanelProvider,
  useArtifactsPanel,
} from '../../context/ArtifactsPanelContext';
import { useGlobalShortcuts } from '../../hooks/useGlobalShortcuts';
import { QuotaProvider } from '../../context/QuotaContext';
import apiService from '../../services/api';

import Sidebar from './Sidebar';
import ContextPanel from './ContextPanel';
import DropZoneOverlay from './DropZoneOverlay';

export type ProjectSummary = { id: string; name: string; document_count: number };

/** Read a boolean from localStorage with a default fallback. */
const readPersistedBool = (key: string, fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* localStorage may be blocked (Safari private mode) — fall through */
  }
  return fallback;
};

const writePersistedBool = (key: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
};

export interface AppShellOutletContext {
  projects: ProjectSummary[];
  refreshProjects: () => Promise<void>;
}

const LEGACY_HASH_VIEWS = new Set(['chat', 'graph', 'valuation', 'underwriting']);

/**
 * Outer shell — mounts all the providers. UI lives in AppShellLayout so it
 * can call useArtifactsPanel / useChatContext from inside the provider tree.
 */
const AppShell: React.FC = () => {
  return (
    <FileTreeProvider>
      <ChatProvider>
        <ArtifactsPanelProvider>
          <QuotaProvider>
            <AppShellLayout />
          </QuotaProvider>
        </ArtifactsPanelProvider>
      </ChatProvider>
    </FileTreeProvider>
  );
};

const AppShellLayout: React.FC = () => {
  const navigate = useNavigate();
  const { clearChat } = useChatContext();
  const { isOpen: isArtifactsOpen, closeArtifact } = useArtifactsPanel();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // The leading-edge (left in RTL) third column — Sources / artifacts.
  // Persisted so the user's arrangement survives reloads.
  const [isContextOpen, setIsContextOpen] = useState(() =>
    readPersistedBool('shell.contextOpen', true),
  );
  const [projects, setProjects] = useState<ProjectSummary[]>([]);

  const setContextOpen = (value: boolean) => {
    setIsContextOpen(value);
    writePersistedBool('shell.contextOpen', value);
  };
  const toggleContextPanel = () => setContextOpen(!isContextOpen);

  // The column is on whenever the user wants Sources OR an artifact is showing
  // (an artifact request forces the column open even if Sources was collapsed).
  const isPanelVisible = isContextOpen || isArtifactsOpen;

  const refreshProjects = async () => {
    try {
      const data = await apiService.getProjects();
      setProjects(data);
    } catch {
      /* sidebar shows empty state on failure; no toast on a passive fetch */
    }
  };

  useEffect(() => {
    refreshProjects();
  }, []);

  // Legacy bookmark redirect — strips the hash for old tab routes (#chat,
  // #graph, #valuation, #underwriting) so the new path-based router can take
  // over. Doesn't change the route; just cleans up the URL.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (LEGACY_HASH_VIEWS.has(hash)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      // For #valuation / #underwriting / #graph the old behavior was to switch
      // to a dedicated tab. The new architecture surfaces those as artifacts
      // requested via chat. Land the user on /c/new with a hint.
      if (hash !== 'chat') {
        navigate('/c/new', { replace: true });
      }
    }
  }, [navigate]);

  // Keyboard shortcuts. Registered here so each callback has fresh state
  // closures (toggle artifacts uses the latest isOpen).
  useGlobalShortcuts({
    onNewChat: () => {
      clearChat();
      navigate('/c/new');
    },
    onToggleSidebar: () => setIsSidebarOpen((v) => !v),
    onToggleArtifacts: toggleContextPanel,
    onEscape: () => {
      if (isArtifactsOpen) closeArtifact();
    },
  });

  const outletContext: AppShellOutletContext = { projects, refreshProjects };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-app">
      <Header
        onMenuToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        isMenuOpen={isMobileSidebarOpen}
        onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isSidebarOpen={isSidebarOpen}
        onContextToggle={toggleContextPanel}
        isContextOpen={isContextOpen}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar — RTL puts this on the right side of the screen */}
        <aside
          className={`hidden md:flex flex-col bg-sidebar-900 border-l border-sidebar-700/50 transition-[width,min-width,max-width] duration-300 ease-drawer overflow-hidden ${
            isSidebarOpen ? 'w-sidebar min-w-sidebar max-w-sidebar' : 'w-0 min-w-0 max-w-0 border-l-0'
          }`}
        >
          {isSidebarOpen && <Sidebar projects={projects} />}
        </aside>

        {/* Mobile sidebar overlay */}
        <div
          className={`fixed inset-0 z-30 bg-black/50 md:hidden transition-opacity duration-200 ${
            isMobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsMobileSidebarOpen(false)}
        />
        <aside
          className={`fixed inset-y-0 right-0 z-40 w-sidebar flex flex-col bg-sidebar-900 md:hidden mt-12 transition-transform duration-200 ease-in-out ${
            isMobileSidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <Sidebar projects={projects} onNavigate={() => setIsMobileSidebarOpen(false)} />
        </aside>

        {/* Main content — pages render here via <Outlet />. Wrapped in
            DropZoneOverlay so files dropped anywhere over the page upload
            via the same pipeline as the 📎 attach button. */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <DropZoneOverlay onUploadComplete={refreshProjects}>
            <Outlet context={outletContext} />
          </DropZoneOverlay>
        </main>

        {/* Context panel — RTL puts this on the leading (left) edge. The
            persistent third column: Sources (documents in the chat's context)
            by default, swapping to an artifact (valuation / underwriting /
            knowledge graph) when the user asks for one. On screens narrower
            than md, artifacts slide up as a bottom sheet. */}
        <ContextPanel isOpen={isPanelVisible} onCollapse={() => setContextOpen(false)} />
      </div>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { background: '#363636', color: '#fff' },
          success: { duration: 3000, iconTheme: { primary: '#10B981', secondary: '#fff' } },
          error: { duration: 5000, iconTheme: { primary: '#EF4444', secondary: '#fff' } },
        }}
      />
    </div>
  );
};

export default AppShell;
